import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Stack
} from '@mui/joy';
import { useAuth } from '@/context/AuthContext';
import { PreferenceService } from '@/services/preferenceService';
import {
  EmployeePreferences,
  PreferenceSummary
} from '@/types/preference';
import DatePreferencesTab from '@/components/preferences/DatePreferencesTab';
import DayPreferencesTab from '@/components/preferences/DayPreferencesTab';
import ShiftTypePreferencesTab from '@/components/preferences/ShiftTypePreferencesTab';
import PreferenceSummaryCard from '@/components/preferences/PreferenceSummaryCard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`preferences-tabpanel-${index}`}
      aria-labelledby={`preferences-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}



const PreferencesPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [preferences, setPreferences] = useState<EmployeePreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<PreferenceSummary | null>(null);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await PreferenceService.getMyPreferences();
      setPreferences(data);
      setSummary(PreferenceService.calculatePreferenceSummary(data));
    } catch (err) {
      console.error('Error loading preferences:', err);
      setError('Failed to load preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  const handlePreferenceUpdate = async () => {
    await loadPreferences();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={loadPreferences} variant="outlined">
          Retry
        </Button>
      </Box>
    );
  }

  if (!preferences) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography level="h4" sx={{ mb: 2 }}>
          Preferences
        </Typography>
        <Alert color="warning">
          No preferences found. Start by adding your scheduling preferences.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography level="h3" sx={{ mb: 3 }}>
        My Preferences
      </Typography>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid xs={12} md={3}>
            <PreferenceSummaryCard
              title="Total Preferences"
              value={summary.total_preferences}
              color="primary"
            />
          </Grid>
          <Grid xs={12} md={3}>
            <PreferenceSummaryCard
              title="Date Preferences"
              value={summary.date_preferences}
              color="success"
            />
          </Grid>
          <Grid xs={12} md={3}>
            <PreferenceSummaryCard
              title="Day Preferences"
              value={summary.day_preferences}
              color="warning"
            />
          </Grid>
        </Grid>
      )}

      {/* Tab Navigation */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Button
            variant={tabValue === 0 ? "solid" : "plain"}
            onClick={() => setTabValue(0)}
            sx={{ borderRadius: 0, borderBottom: tabValue === 0 ? 2 : 0, borderColor: 'primary.500' }}
          >
            Date Preferences
            {preferences.date_preferences.length > 0 && (
              <Chip size="sm" color="primary" sx={{ ml: 1 }}>
                {preferences.date_preferences.length}
              </Chip>
            )}
          </Button>
          <Button
            variant={tabValue === 1 ? "solid" : "plain"}
            onClick={() => setTabValue(1)}
            sx={{ borderRadius: 0, borderBottom: tabValue === 1 ? 2 : 0, borderColor: 'primary.500' }}
          >
            Day Preferences
            {preferences.day_preferences.length > 0 && (
              <Chip size="sm" color="warning" sx={{ ml: 1 }}>
                {preferences.day_preferences.length}
              </Chip>
            )}
          </Button>
          <Button
            variant={tabValue === 2 ? "solid" : "plain"}
            onClick={() => setTabValue(2)}
            sx={{ borderRadius: 0, borderBottom: tabValue === 2 ? 2 : 0, borderColor: 'primary.500' }}
          >
            Shift Type Preferences
            {preferences.shift_type_preferences.length > 0 && (
              <Chip size="sm" color="success" sx={{ ml: 1 }}>
                {preferences.shift_type_preferences.length}
              </Chip>
            )}
          </Button>
        </Stack>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <DatePreferencesTab
          preferences={preferences.date_preferences}
          onUpdate={handlePreferenceUpdate}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <DayPreferencesTab
          preferences={preferences.day_preferences}
          onUpdate={handlePreferenceUpdate}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <ShiftTypePreferencesTab
          preferences={preferences.shift_type_preferences}
          onUpdate={handlePreferenceUpdate}
        />
      </TabPanel>

      {/* Help Section */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography level="title-sm" sx={{ mb: 2 }}>
            How Preferences Work
          </Typography>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            • <strong>Date Preferences:</strong> Mark specific dates as unavailable or preferred
          </Typography>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            • <strong>Day Preferences:</strong> Set preferences for days of the week
          </Typography>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            • <strong>Shift Type Preferences:</strong> Indicate which shift types you prefer or want to avoid
          </Typography>
          <Typography level="body-sm" sx={{ mt: 2 }}>
            Managers will see these preferences when creating schedules and will try to accommodate them when possible.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PreferencesPage; 